import { useLocation, useNavigate } from "react-router-dom";
import { ContainerCard } from "../../components/ui/cards";
import { ChevronLeftIcon, Save, X } from "lucide-react";
import { useCallback, useEffect, useState, useRef } from "react";
import { ASSET_REGISTRY, EMPLOYEE_PERSONAL_INFO, ASSET_EMPLOYEE_AUDIT } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import { Textarea } from "../../components/ui/input";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";

const AssetEmployeeAuditAddEdit = () => {
    let location = useLocation()
    let navigate = useNavigate()
    const { fetch, create, update } = useApiMethods()
    const { fromEdit, editDetails, isActive } = location.state || {};

    const [formData, setFormData] = useState({
        id: "",
        employee: "",
        asset_registry: "",
        issued_date: "",
        return_date: "",
        handing_over_by: "",
        handing_over_to: "",
        approved_by: "",
        remarks: "",
        is_active: true,
        company_id: sessionStorage.getItem("userId")
    });

    const [dropdownData, setDropdownData] = useState({
        assetRegistryList: [],
        employeeList: [],
    });

    const [loading, setLoading] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const prevPropsRef = useRef();

    // Fetch dropdown data
    const getAssetRegistryList = useCallback(async () => {
        try {
            const apiData = await fetch(ASSET_REGISTRY, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            } else {
                ToastMsg('error', 'Failed to load asset registry');
                return [];
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const getAssetEmployeeList = useCallback(async () => {
        try {
            const apiData = await fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: 'True' });
            if (apiData?.status === "success" && apiData.data) {
                return apiData.data;
            } else {
                ToastMsg('error', 'Failed to load employees');
                return [];
            }
        } catch (error) {
            ToastMsg('error', error.message);
            return [];
        }
    }, [fetch]);

    const fetchDropdownData = async () => {
        setLoading(true);
        try {
            const [assetRegistryList, employeeList] = await Promise.all([
                getAssetRegistryList(),
                getAssetEmployeeList()
            ]);

            setDropdownData({
                assetRegistryList: assetRegistryList || [],
                employeeList: employeeList || [],
            });

        } catch (error) {
            ToastMsg('error', 'Failed to load dropdown data: ' + error.message);
        } finally {
            setLoading(false);
        }
    }

    // Enhanced edit data population
    useEffect(() => {
        if (fromEdit && editDetails &&
            Object.keys(editDetails).length !== 0 &&
            JSON.stringify(prevPropsRef.current) !== JSON.stringify(editDetails)) {

            const editData = {
                id: editDetails.id,
                employee: editDetails.employee?.id || "",
                asset_registry: editDetails.asset_registry_id?.id || "",
                handing_over_to: editDetails.handing_over_to_id?.employee_id || "",
                handing_over_by: editDetails.hand_over_by?.employee_id || "",
                approved_by: editDetails.approved_by_id?.employee_id || "",
                remarks: editDetails.remarks || "",
                issued_date: editDetails.issued_date ? dayjs(editDetails.issued_date) : "",
                return_date: editDetails.return_date ? dayjs(editDetails.return_date) : "",
                is_active: editDetails.is_active,
                company_id: sessionStorage.getItem("userId")
            };

            setFormData(editData);
            prevPropsRef.current = editDetails;
        }
    }, [fromEdit, editDetails]);

    useEffect(() => {
        fetchDropdownData()
    }, [])

    const handleBack = () => {
        navigate(-1)
    }

    const handleInputChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // Date restriction functions from old code
    const restrictIssuedDate = (current) => {
        if (formData.asset_registry) {
            const item = dropdownData.assetRegistryList.find(item => item.id === parseInt(formData.asset_registry));
            if (item?.purchase_date) {
                const startDate = dayjs(item.purchase_date);
                if (startDate) {
                    return current && current < startDate.startOf('day');
                }
            }
        }
        return current && current > dayjs().endOf('day');
    };

    const disabledReturnDate = (current) => {
        const { issued_date } = formData;
        if (issued_date) {
            const startDate = dayjs(issued_date);
            return current && current < startDate.startOf('day');
        }
        return false;
    };

    // Filter assets that are not assigned (from old code)
    const getAvailableAssets = () => {
        return dropdownData.assetRegistryList.filter(asset =>
            (!asset.is_assigned && asset.is_active) || asset.id === formData.asset_registry
        );
    };

    const handleSave = async () => {
        setLoading(true);
        setIsDisabled(true);

        try {
            // Basic validation
            if (!formData.employee || !formData.asset_registry || !formData.issued_date || !formData.handing_over_by) {
                ToastMsg('error', 'Please fill all required fields');
                setLoading(false);
                setIsDisabled(false);
                return;
            }

            // Transform data for API
            const submitData = {
                ...formData,
                issued_date: formData.issued_date ? dayjs(formData.issued_date).format('YYYY-MM-DD') : null,
                return_date: formData.return_date ? dayjs(formData.return_date).format('YYYY-MM-DD') : null,
                is_draft: false
            };

            if (fromEdit && editDetails?.id) {
                submitData.id = editDetails.id;
            }

            // Remove empty strings
            Object.keys(submitData).forEach(key => {
                if (submitData[key] === "") {
                    submitData[key] = null;
                }
            });

            const apiFunction = fromEdit ? update : create;
            const response = await apiFunction(ASSET_EMPLOYEE_AUDIT, submitData);

            if (response?.status === "success") {
                ToastMsg("success", response.message);
                handleBack();
            } else {
                ToastMsg("error", response?.message || "Failed to save audit record");
            }

        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setLoading(false);
            setIsDisabled(false);
        }
    };

    if (loading && dropdownData.assetRegistryList.length === 0) {
        return (
            <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex items-center justify-center rounded-md">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
            </div>
        );
    }

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <ChevronLeftIcon className="h-5 w-5 cursor-pointer" onClick={handleBack} />
                <div className="flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {fromEdit ? "Edit Employee Audit" : "Add Employee Audit"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {fromEdit ? "Update asset employee audit details" : "Create new asset employee audit record"}
                    </p>
                </div>
            </div>

            {/* Form Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
                    {/* Employee ID */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Employee ID <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.employee}
                            onChange={(value) => handleInputChange('employee', value)}
                            size="large"
                            placeholder="Select Employee"
                            options={dropdownData.employeeList
                                .filter(emp => emp.is_active || emp.id === formData.employee)
                                .map(emp => ({
                                    label: `${emp.first_name} ${emp.last_name} - ${emp.employee_id}`,
                                    value: emp.id
                                }))
                            }
                            showSearch
                            filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            )}
                        />
                    </div>

                    {/* Asset ID */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Asset ID <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.asset_registry}
                            onChange={(value) => handleInputChange('asset_registry', value)}
                            size="large"
                            placeholder="Select Asset"
                            options={dropdownData.assetRegistryList.map(asset => ({
                                label: `${asset.asset_id} - ${asset.asset_name}`,
                                value: asset.id
                            }))}
                            showSearch
                            filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            )}
                        />
                    </div>

                    {/* Issued Date */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Issued Date <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                            value={formData.issued_date ? dayjs(formData.issued_date) : null}
                            onChange={(value) => handleInputChange('issued_date', value)}
                            disabledDate={restrictIssuedDate}
                            format="YYYY-MM-DD"
                            size="large"
                            placeholder="Select Issued Date"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            )}
                        />
                    </div>

                    {/* Return Date */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Return Date {formData.handing_over_to && <span className="text-red-500">*</span>}
                        </label>
                        <DatePicker
                            value={formData.return_date ? dayjs(formData.return_date) : null}
                            onChange={(value) => handleInputChange('return_date', value)}
                            disabledDate={disabledReturnDate}
                            format="YYYY-MM-DD"
                            size="large"
                            placeholder="Select Return Date"
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            )}
                        />
                    </div>

                    {/* Handover By */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Handover By <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.handing_over_by}
                            onChange={(value) => handleInputChange('handing_over_by', value)}
                            size="large"
                            placeholder="Select Employee"
                            options={dropdownData.employeeList
                                .filter(emp => emp.is_active || emp.id === formData.handing_over_by)
                                .map(emp => ({
                                    label: `${emp.first_name} ${emp.last_name} - ${emp.employee_id}`,
                                    value: emp.id
                                }))
                            }
                            showSearch
                            filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            )}
                        />
                    </div>

                    {/* Handover To */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Handover To
                        </label>
                        <Select
                            value={formData.handing_over_to}
                            onChange={(value) => handleInputChange('handing_over_to', value)}
                            size="large"
                            placeholder="Select Employee"
                            options={dropdownData.employeeList
                                .filter(emp => emp.is_active || emp.id === formData.handing_over_to)
                                .map(emp => ({
                                    label: `${emp.first_name} ${emp.last_name} - ${emp.employee_id}`,
                                    value: emp.id
                                }))
                            }
                            showSearch
                            filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            )}
                        />
                    </div>

                    {/* Approved By */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Approved By
                        </label>
                        <Select
                            value={formData.approved_by}
                            onChange={(value) => handleInputChange('approved_by', value)}
                            size="large"
                            placeholder="Select Employee"
                            options={dropdownData.employeeList
                                .filter(emp => emp.is_active || emp.id === formData.approved_by)
                                .map(emp => ({
                                    label: `${emp.first_name} ${emp.last_name} - ${emp.employee_id}`,
                                    value: emp.id
                                }))
                            }
                            showSearch
                            filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            )}
                        />
                    </div>

                    {/* Remarks - Full width */}
                    <div className="md:col-span-2 space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Remarks
                        </label>
                        <Textarea
                            value={formData.remarks}
                            onChange={(e) => handleInputChange('remarks', e.target.value)}
                            placeholder="Enter additional remarks or description"
                            rows={4}
                            maxLength={500}
                            className={cn(
                                "w-full rounded-md transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            )}
                        />
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Button
                        variant="outline"
                        onClick={handleBack}
                        disabled={loading || isDisabled}
                        className="flex items-center gap-2"
                    >
                        <X className="w-4 h-4" />
                        Cancel
                    </Button>

                    <Button
                        variant="brand"
                        onClick={handleSave}
                        disabled={loading || isDisabled}
                        className="flex items-center gap-2"
                    >
                        {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        {loading ? "Saving..." : (fromEdit ? "Update" : "Save")}
                    </Button>
                </div>
            </div>
        </ContainerCard>
    );
}

export default AssetEmployeeAuditAddEdit;